import AnimatedSection from "@/components/landing/AnimatedSection";
import { type Persona, personaContent } from "@/lib/persona-content";

interface PainPointsSectionProps {
  persona: Persona;
}

export function PainPointsSection({ persona }: PainPointsSectionProps) {
  const content = personaContent[persona];

  // Parse the section header — text inside <muted> tags gets styled differently
  const headerParts = content.sectionHeader.split(/<muted>|<\/muted>/);

  return (
    <section className="py-20 md:py-28 bg-gradient-to-b from-background to-[hsl(220,25%,6%)]">
      <div className="container">
        {/* Section Header */}
        <AnimatedSection>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-display font-bold text-center max-w-3xl mx-auto mb-16 leading-snug">
            {headerParts.map((part, i) =>
              i % 2 === 1 ? (
                <span key={i} className="text-muted-foreground">
                  {part}
                </span>
              ) : (
                <span key={i}>{part}</span>
              )
            )}
          </h2>
        </AnimatedSection>

        {/* Pain Points Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {content.painPoints.map((point, index) => (
            <AnimatedSection key={point.title} delay={index * 100}>
              <div className="flex flex-col p-6 rounded-2xl bg-card border border-border/50 h-full">
                <span className="text-5xl mb-4">{point.emoji}</span>
                <h3 className="text-xl font-display font-semibold mb-3">
                  {point.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {point.description}
                </p>
              </div>
            </AnimatedSection>
          ))}
        </div>

        {/* Transition Statement */}
        <AnimatedSection>
          <div className="text-center space-y-2">
            <p className="text-lg md:text-xl text-muted-foreground">
              The problem isn't motivation. It's lack of system.
            </p>
            <p className="text-xl md:text-2xl font-display font-semibold gradient-text">
              {content.sectionTransition}
            </p>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}
